import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { apiUrl } from '../utils/api'
import {
  ArrowLeft,
  CheckCircle2,
  BookOpen,
  HelpCircle,
  AlertCircle,
  Trophy,
  RotateCcw,
  Clock,
  ChevronRight,
  BadgeCheck,
  Trash2,
  ListTodo,
} from 'lucide-react'
import '../styles/course.css'

interface Module {
  id: string
  headline: string
  summary: string | null
  subheadline: string | null
  heroImageUrl: string | null
  readTime: number | null
  hasQuiz: boolean
  quizQuestionCount: number
  read: boolean
  quizAttempted: boolean
  quizBestScore: number
  quizBestPercentage: number
  done: boolean
  locked: boolean
}

interface CourseInfo {
  id: string
  title: string
  summary: string | null
  cover_image_url: string | null
  total_modules: number
}

interface Progress {
  completed: number
  total: number
  percentage: number
}

interface Final {
  allDone: boolean
  hasQuizzes: boolean
  correct: number
  total: number
  percentage: number
  passed: boolean
}

export default function CourseDetail() {
  const { courseId } = useParams()
  const { token } = useAuth()
  const navigate = useNavigate()

  const [course, setCourse] = useState<CourseInfo | null>(null)
  const [modules, setModules] = useState<Module[]>([])
  const [progress, setProgress] = useState<Progress | null>(null)
  const [final, setFinal] = useState<Final | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [retaking, setRetaking] = useState(false)

  const fetchDetail = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await fetch(apiUrl(`/api/courses/${courseId}`), {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to load course')
      setCourse(data.course)
      setModules(Array.isArray(data.modules) ? data.modules : [])
      setProgress(data.progress)
      setFinal(data.final || null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load course')
    } finally {
      setIsLoading(false)
    }
  }, [courseId, token])

  useEffect(() => { fetchDetail() }, [fetchDetail])

  const handleRetake = async () => {
    if (!confirm('Reset all progress and quiz scores for this course?')) return
    setRetaking(true)
    try {
      const res = await fetch(apiUrl(`/api/courses/${courseId}/retake`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to reset course')
      }
      await fetchDetail()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset course')
    } finally {
      setRetaking(false)
    }
  }

  if (isLoading) return <div className="loading">Loading course…</div>

  if (error && !course) {
    return (
      <div className="course-detail-container">
        <div className="error-message" style={{ maxWidth: 900, margin: '40px auto', padding: '0 24px' }}>{error}</div>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px' }}>
          <button className="back-link-btn" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={15} /> Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  const readCount = modules.filter((m) => m.read).length
  const quizModules = modules.filter((m) => m.hasQuiz)
  const quizPassedCount = quizModules.filter((m) => m.quizBestPercentage >= 75).length
  const pendingLessons = modules.filter((m) => !m.read).length
  
  // Calculate progress based on modules read (not exceeding 100%)
  const calculatedProgress = modules.length > 0 
    ? Math.min(Math.round((readCount / modules.length) * 100), 100)
    : 0

  return (
    <div className="course-detail-container">
      {/* Hero */}
      <header
        className="course-hero"
        style={
          course?.cover_image_url
            ? { backgroundImage: `linear-gradient(rgba(15,14,23,0.55),rgba(15,14,23,0.82)), url(${course.cover_image_url})` }
            : undefined
        }
      >
        <div className="course-hero-top-bar">
          <button className="back-link-btn light" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={15} /> Dashboard
          </button>
          {calculatedProgress === 100 && (
            <div className="course-complete-pill">
              <BadgeCheck size={15} /> Course Complete
            </div>
          )}
          <button 
            className="reset-course-btn" 
            onClick={handleRetake} 
            disabled={retaking}
            title="Reset course"
          >
            <Trash2 size={16} />
          </button>
        </div>
        <h1>{course?.title}</h1>
        {course?.summary && <p className="course-hero-summary">{course.summary}</p>}
        {modules.length > 0 && (
          <div className="course-hero-progress">
            <div className="progress-bar light">
              <div className="progress-fill" style={{ width: `${calculatedProgress}%` }} />
            </div>
            <span>{readCount}/{modules.length} lessons complete · {calculatedProgress}%</span>
          </div>
        )}
      </header>

      {/* Final result banner */}
      {final && final.allDone && final.hasQuizzes && (
        <div className={`final-result ${final.passed ? 'passed' : 'failed'}`}>
          <div className="final-result-content">
            <div className="final-result-icon">
              {final.passed ? <Trophy size={32} /> : <AlertCircle size={32} />}
            </div>
            <div className="final-result-body">
              {final.passed ? (
                <>
                  <h2>🎉 Course Passed!</h2>
                  <p>Excellent work! You scored <strong>{final.correct}/{final.total}</strong> ({final.percentage}%) across all quizzes — well above the 75% pass mark.</p>
                </>
              ) : (
                <>
                  <h2>Keep Going!</h2>
                  <p>Your final score is <strong>{final.correct}/{final.total}</strong> ({final.percentage}%). You need 75% to pass — redo any failed quizzes to improve.</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="module-list">
        {/* Enhanced stats box */}
        <div className="course-stats-box">
          <div className="stat-card progress-stat">
            <div className="stat-icon">
              <BookOpen size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{readCount}</div>
              <div className="stat-label">Lessons Complete</div>
            </div>
          </div>
          
          <div className="stat-card pending-stat">
            <div className="stat-icon">
              <ListTodo size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{pendingLessons}</div>
              <div className="stat-label">Lessons Pending</div>
            </div>
          </div>

          {quizModules.length > 0 && (
            <div className="stat-card quiz-stat">
              <div className="stat-icon">
                <HelpCircle size={20} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{quizPassedCount}/{quizModules.length}</div>
                <div className="stat-label">Quizzes Passed</div>
              </div>
            </div>
          )}
        </div>

        <h2>Lessons</h2>

        {modules.length === 0 ? (
          <p className="no-courses">This course has no lessons yet.</p>
        ) : (
          <ol className="modules">
            {modules.map((m, idx) => (
              <li key={m.id} className={`module-row ${m.done ? 'completed' : ''}`}>
                {/* Status circle */}
                <div className="module-index">
                  {m.done
                    ? <CheckCircle2 size={18} />
                    : m.read
                    ? <BookOpen size={16} />
                    : <span>{idx + 1}</span>}
                </div>

                {/* Info — click to read */}
                <div className="module-info" onClick={() => navigate(`/course/${courseId}/module/${m.id}`)}>
                  <h3>{m.headline}</h3>
                  {m.subheadline && <p>{m.subheadline}</p>}
                  <div className="module-tags">
                    {m.readTime && (
                      <span className="read-time"><Clock size={11} /> {m.readTime} min</span>
                    )}
                    {m.read && !m.done && !m.hasQuiz && (
                      <span className="tag-read">Read</span>
                    )}
                  </div>
                </div>

                {/* Quiz status / action */}
                {m.hasQuiz && (
                  <div className="module-quiz-col">
                    {m.quizAttempted ? (
                      <button
                        className={`quiz-status-btn ${m.quizBestPercentage >= 75 ? 'passed' : 'failed'}`}
                        onClick={() => navigate(`/course/${courseId}/module/${m.id}/quiz`)}
                        title={`Best: ${m.quizBestPercentage}%`}
                      >
                        {m.quizBestPercentage >= 75
                          ? <><CheckCircle2 size={13} /> {m.quizBestPercentage}%</>
                          : <><AlertCircle size={13} /> {m.quizBestPercentage}% — Retry</>}
                      </button>
                    ) : (
                      <button
                        className="quiz-status-btn neutral"
                        onClick={() => navigate(`/course/${courseId}/module/${m.id}/quiz`)}
                      >
                        <HelpCircle size={13} /> Quiz
                      </button>
                    )}
                  </div>
                )}

                {/* Read chevron */}
                <button
                  className="module-read-btn"
                  onClick={() => navigate(`/course/${courseId}/module/${m.id}`)}
                  title="Read lesson"
                >
                  <ChevronRight size={18} />
                </button>
              </li>
            ))}
          </ol>
        )}
      </main>
    </div>
  )
}


interface Module {
  id: string
  headline: string
  summary: string | null
  subheadline: string | null
  heroImageUrl: string | null
  readTime: number | null
  hasQuiz: boolean
  quizQuestionCount: number
  read: boolean
  quizAttempted: boolean
  quizBestScore: number
  quizBestPercentage: number
  done: boolean
  locked: boolean
}

interface CourseInfo {
  id: string
  title: string
  summary: string | null
  cover_image_url: string | null
  total_modules: number
}

interface Progress {
  completed: number
  total: number
  percentage: number
}

interface Final {
  allDone: boolean
  hasQuizzes: boolean
  correct: number
  total: number
  percentage: number
  passed: boolean
}
